import { spawn, ActorRefFrom } from 'xstate';
import { DataAccess } from '@chargify/data-access';

import { createSubscriptionMachine } from '../subscription';
import { createComponentsMachine } from '../components';

import {
  serializeSubscription,
  SubscriptionEntity,
  SubscriptionModel,
} from '../../models';

import { DEFAULT_VISIBLE_SUBSCRIPTIONS } from './constants';
import { Catalog } from './types';
import { createCustomFieldsMachine } from '../customFields/customFields';

/**
 * Creates a collection of subscriptions
 * @param {Array} items - collection of subscriptions from API
 * @returns Subscriptions collection
 */
export const serializeSubscriptions = (
  items: Record<string, any>,
  dataAccess: DataAccess
) => {
  const subscriptions: Record<
    number,
    {
      subscription: SubscriptionModel;
      componentsRef: ActorRefFrom<typeof createComponentsMachine>;
      subscriptionRef: ActorRefFrom<typeof createSubscriptionMachine>;
      customFieldsRef: ActorRefFrom<typeof createCustomFieldsMachine>;
    }[]
  > = {};

  items.forEach((subscription: SubscriptionEntity) => {
    const serializedSubscription = serializeSubscription(subscription);
    const { id: subscriptionId } = serializedSubscription;

    const componentsMachine = createComponentsMachine(
      subscriptionId
    ).withConfig({
      services: {
        getComponents: ({ subscriptionId }) =>
          dataAccess.getComponents(subscriptionId),
      },
    });

    const subscriptionMachine = createSubscriptionMachine(
      serializedSubscription
    );

    const customFieldsMachine = createCustomFieldsMachine(
      subscriptionId
    ).withConfig({
      services: {
        getSubscriptionCustomFields: ({ subscriptionId }) => {
          return dataAccess.getSubscriptionCustomFields(subscriptionId);
        },
        saveCustomerCustomFields: ({ subscriptionId }, { payload: fields }) => {
          return dataAccess.updateSubscriptionCustomFields(
            subscriptionId,
            fields
          );
        },
      },
    });

    const entity = {
      subscription: serializedSubscription,
      componentsRef: spawn(
        componentsMachine,
        `subscription-components-${subscriptionId}`
      ),
      subscriptionRef: spawn(
        subscriptionMachine,
        `subscription-${serializedSubscription.id}`
      ),
      customFieldsRef: spawn(
        customFieldsMachine,
        `custom-fields-${subscriptionId}`
      ),
    };

    if (subscriptions[subscription.productFamilyId]) {
      subscriptions[subscription.productFamilyId].push(entity);
    } else {
      subscriptions[subscription.productFamilyId] = [entity];
    }
  });

  return subscriptions;
};

/**
 * Creates a pagination settings for product catalogs
 * @param {Record} subscriptions - collection of subscriptions
 * @returns Catalog with pagination settings
 */
export const serializeCatalogs = (subscriptions: {
  [catalogId: number]: Record<string, any>;
}): Record<string, Catalog> =>
  Object.keys(subscriptions).reduce((acc, catalogId) => {
    (acc as Record<number, Catalog>)[Number(catalogId)] = {
      isPaginated:
        subscriptions[Number(catalogId)].length > DEFAULT_VISIBLE_SUBSCRIPTIONS,
      isListExpanded: false,
    };
    return acc;
  }, {});
